import { useEffect } from 'react';
import urlTemplate from 'url-template';
import type {
  BulletinBoardNoticesParams,
  BulletinBoardNoticesResponse,
} from 'src/apis/secondary-market-service/types';
import type {
  OptionalSecondaryMarketServiceRequest,
  SecondaryMarketServiceRequest,
} from 'src/apis/secondary-market-service/use-secondary-market-service';
import { useAxios } from 'src/hooks/use-axios';
import { isConstraintViolation } from 'src/utils/is-constraint-violation';

type HookConfig = {
  secondaryMarketService:
    | SecondaryMarketServiceRequest
    | OptionalSecondaryMarketServiceRequest;
  params: BulletinBoardNoticesParams;
};

type ExecuteProps = {
  url: string;
  params: BulletinBoardNoticesParams;
};

export function useBulletinBoardNotices({
  secondaryMarketService,
  params,
}: HookConfig) {
  const request = useAxios(
    (axios, baseConfig, { url, params }: ExecuteProps) =>
      axios.request<BulletinBoardNoticesResponse>({
        ...baseConfig,
        url,
        params,
      }),
    {
      neededOnPageLoad: true,
      onError(error) {
        if (isConstraintViolation(error)) return error;
        throw error;
      },
    }
  );

  useEffect(() => {
    if (
      !secondaryMarketService.response ||
      !secondaryMarketService.response.data._links.bulletinBoardNotices?.href
    )
      return;

    const url = urlTemplate
      .parse(
        secondaryMarketService.response.data._links.bulletinBoardNotices.href
      )
      .expand({});

    request.execute({
      url,
      params,
    });
    // oxlint-disable-next-line exhaustive-deps
  }, [secondaryMarketService.response, params]);

  return request;
}

export type BulletinBoardNoticesRequest = ReturnType<
  typeof useBulletinBoardNotices
>;
